import { useRef, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  useApplicationDetails,
  useApplicationMessages,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createApplicationMessage } from 'app/frontend/base/api/application'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'

type Props = {
  teamId?: number
  applicationId: number
  type: 'messages' | 'notes'
  lastRead?: string | null
  onNewMessage?: () => void
  memberReadonly?: boolean
}

const ApplicationMessages = ({
  teamId,
  applicationId,
  type,
  lastRead,
  onNewMessage,
  memberReadonly,
}: Props) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { data: applicationDetails } = useApplicationDetails(
    teamId,
    applicationId,
  )
  const { data: messages } = useApplicationMessages(
    true,
    type,
    teamId ?? applicationDetails?.teamId,
    applicationId,
  )
  const applicantUserId = applicationDetails?.userReference?.id
  const [sentMessage, setSentMessage] = useState(false)
  const [messageInput, setMessageInput] = useState('')
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const dividerRef = useRef<HTMLDivElement>(null)
  const hasScrolled = useRef(false)
  const queryClient = useQueryClient()

  const formatMessageTime = (timestamp: string | Date) => {
    const messageDate = dayjs(timestamp)
    const now = dayjs()
    const hoursDiff = now.diff(messageDate, 'hour')

    if (hoursDiff < 24) {
      return messageDate.format('HH:mm')
    } else {
      return messageDate.format('MMM D')
    }
  }

  const firstUnreadIndex = messages?.findIndex((message) =>
    lastRead ? dayjs(message.createdAt).isAfter(dayjs(lastRead)) : true,
  )

  const { mutate: sendMessage, isPending } = useMutation({
    mutationFn: () => {
      if (!teamId && !applicationDetails?.teamId)
        return Promise.reject(new Error('No team ID found'))

      return createApplicationMessage(
        (teamId ?? applicationDetails?.teamId) as number,
        applicationId,
        messageInput,
        type === 'notes',
      )
    },
    onSuccess: async () => {
      setMessageInput('')
      setSentMessage(true)
      await queryClient.invalidateQueries({
        queryKey: ['applicationMessages', { id: applicationId, type }],
      })
      onNewMessage?.()

      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight
        }
      })
    },
    onError: (error) => displayErrorToast(error, t),
  })

  const handleSend = () => {
    if (!messageInput.trim()) return
    sendMessage()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  useEffect(() => {
    if (hasScrolled.current || !messages) return

    requestAnimationFrame(() => {
      if (dividerRef.current) {
        dividerRef.current.scrollIntoView({ block: 'start' })
        hasScrolled.current = true
      } else if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight
        hasScrolled.current = true
      }
    })
  }, [messages])

  return (
    <div className='flex flex-col h-full'>
      <div
        ref={scrollContainerRef}
        className='flex-1 overflow-y-auto space-y-3 min-h-0'
      >
        {messages?.length === 0 ? (
          <div className='text-center text-muted-foreground py-8'>
            No {type === 'messages' ? 'messages' : 'notes'} yet
          </div>
        ) : (
          messages?.map((message, index) => {
            const isCurrentUser = message.userReference?.id === user?.id
            const isFirstUnread =
              firstUnreadIndex !== undefined &&
              firstUnreadIndex !== -1 &&
              index === firstUnreadIndex

            return (
              <div key={message.id} className='pr-6'>
                {isFirstUnread && !isPending && !sentMessage && (
                  <div
                    ref={dividerRef}
                    className='flex items-center gap-3 my-4'
                  >
                    <div className='flex-1 h-px bg-destructive-foreground/50' />
                    <span className='text-xs text-destructive-foreground font-medium'>
                      New messages
                    </span>
                    <div className='flex-1 h-px bg-destructive-foreground/50' />
                  </div>
                )}
                <div
                  className={`flex ${
                    isCurrentUser ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg py-2 px-3 border ${
                      isCurrentUser
                        ? 'bg-accent'
                        : message.userReference?.id === applicantUserId
                        ? 'bg-primary/20'
                        : 'bg-secondary'
                    }`}
                  >
                    <div className='min-w-40 text-xs text-muted-foreground mb-1 flex justify-between gap-4'>
                      <span>
                        {message.userReference?.obfuscatedBattletag ||
                          'Unknown'}{' '}
                      </span>
                      <TooltipProvider>
                        <Tooltip delayDuration={250}>
                          <TooltipTrigger asChild>
                            <span tabIndex={-1}>
                              {formatMessageTime(message.createdAt)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side={isCurrentUser ? 'left' : 'right'}
                          >
                            {dayjs(message.createdAt).format(
                              'MMM D, YYYY [at] HH:mm',
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className='whitespace-pre-wrap wrap-break-word'>
                      {message.message || ''}
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <ProtectedContent
        section='applications'
        requirement={
          user?.id === applicantUserId
            ? 'none'
            : memberReadonly
            ? 'admin'
            : 'member'
        }
        renderUnauthorized={false}
      >
        <div className='border-t pt-4 mt-4 pr-6'>
          <div className='flex gap-2'>
            <Input
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                type === 'messages' ? 'Type a message...' : 'Type a note...'
              }
              className='flex-1 px-3 py-2 rounded-md border bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring'
            />
            <Button
              disabled={!messageInput.trim() || isPending}
              onClick={handleSend}
              loading={isPending}
            >
              <span>Send</span>
            </Button>
          </div>
        </div>
      </ProtectedContent>
    </div>
  )
}

export default ApplicationMessages
